import { Extension, Mark } from "@tiptap/core";
import {
  Plugin as ProseMirrorPlugin,
  PluginKey as ProseMirrorPluginKey,
} from "@tiptap/pm/state";
import { TextSelection } from "@tiptap/pm/state";
import { isValidURL } from "@/utils/utils";

export const TiptapCustomLinkMark = Mark.create({
  name: "link",
  addOptions() {
    return {
      HTMLAttributes: {
        class: "text-primary-n-400! underline cursor-pointer",
        target: "_blank",
        rel: "noopener noreferrer",
      },
    };
  },
  addAttributes() {
    return {
      href: {
        default: null,
      },
    };
  },
  parseHTML() {
    return [
      {
        tag: "a[href]",
      },
    ];
  },
  renderHTML({ HTMLAttributes }) {
    return ["a", HTMLAttributes, 0];
  },
});

export const TiptapLinkExtension = Extension.create({
  name: "urlFormatter",
  addProseMirrorPlugins() {
    return [
      new ProseMirrorPlugin({
        key: new ProseMirrorPluginKey("urlFormatter"),
        props: {
          handlePaste(view, event: ClipboardEvent) {
            const url = event.clipboardData?.getData("text/plain");

            if (url) {
              if (isValidURL(url)) {
                event.preventDefault();

                const { tr } = view.state;
                const from = view.state.selection.from;
                tr.insertText(url);
                tr.addMark(
                  from,
                  from + url.length,
                  view.state.schema.marks.link.create({
                    href: url,
                  }),
                );
                const endPos = from + url.length;
                tr.setSelection(TextSelection.create(tr.doc, endPos));
                view.dispatch(tr);

                return true;
              }
            }

            return false;
          },
          handleDOMEvents: {
            input(view) {
              requestAnimationFrame(() => {
                const { state, dispatch } = view;
                const { $from } = state.selection;

                const textBefore = $from.parent.textBetween(
                  0,
                  $from.parentOffset,
                  "\0",
                  "\0",
                );

                if (textBefore.endsWith(" ")) {
                  // When space is added, always break any existing links first
                  const linkMark = state.schema.marks.link;
                  const linkAtPos = $from
                    .marks()
                    .find((mark) => mark.type === linkMark);

                  if (linkAtPos) {
                    // We're inside a link, so break it
                    const tr = state.tr;
                    const pos = $from.pos;
                    tr.removeMark(pos - 1, pos, linkMark);
                    dispatch(tr);
                  } else {
                    // Check if there's a valid URL before the space that should become a link
                    const match = textBefore.match(/(?:^|\s)([^\s]+)\s$/);

                    if (match) {
                      const url = match[1];
                      const urlStart =
                        $from.start() + textBefore.lastIndexOf(url);
                      const urlEnd = urlStart + url.length;

                      if (isValidURL(url)) {
                        const tr = state.tr;
                        tr.addMark(
                          urlStart,
                          urlEnd,
                          state.schema.marks.link.create({
                            href: url,
                          }),
                        );
                        dispatch(tr);
                      }
                    }
                  }
                }
              });

              return false;
            },
          },
        },
      }),
    ];
  },
});
